import React, { useEffect, useState ,useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './Dashboard.css';
import SmartStack from './SmartStack';
import RecentActivitiesWidget from './RecentActivitiesWidget';
import InsightsWidget from './InsightsWidget';
import StatusWidget from './StatusWidget';
import MoodTracker from './MoodTracker';
import ModelStatusBanner from './ModelStatusBanner';
import { useModelStatus } from '../hooks/useModelStatus';
import { useOverview } from '../hooks/useOverview';
import { API, authFetch, getAuth } from '../api';
import {
  hasValidOverviewCache,
  loadStoredOverview,
  storeOverview,
} from '../utils/overviewStore';

function Dashboard() {
  const navigate = useNavigate();

  // Init handshake — kicks off the model run on this screen and tells
  // the banner whether to show a "computing…" notice.
  const {
    status: modelStatus,
    phase: modelPhase,
    refresh: refreshModel,
  } = useModelStatus();

  const { data: overview, refresh: refreshOverview } = useOverview();

  const [displayOverview, setDisplayOverview] = useState(() =>
    hasValidOverviewCache() ? loadStoredOverview() : null
  );

  useEffect(() => {
    if (overview) {
      setDisplayOverview(overview);
      storeOverview(overview);
    }
  }, [overview]);

 
  const ACTUAL_AGE_CACHE_KEY = 'hrv4_actual_age_cache_v1';
  const [actualAge, setActualAge] = useState(() => {
    try {
      const raw = localStorage.getItem(ACTUAL_AGE_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const cachedAge = Number(parsed?.age);
      return Number.isFinite(cachedAge) && cachedAge > 0 ? cachedAge : null;
    } catch (_) {
      return null;
    }
  });
  const prevModelStatusRef = useRef(null);
useEffect(() => {
  const prev = prevModelStatusRef.current;
  const wasRunning = prev === 'STARTED' || prev === 'RUNNING';
  if (wasRunning && modelStatus === 'NOTHING') {
    refreshOverview();
  }
  prevModelStatusRef.current = modelStatus;
}, [modelStatus, refreshOverview]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const auth = getAuth();
        if (!auth || !auth.userId) return;
        const res = await authFetch(API.userById(auth.userId), { method: 'GET' });
        if (!res.ok) return;
        const profile = await res.json();
        const ageNum = Number(profile?.age);
        if (!cancelled && Number.isFinite(ageNum) && ageNum > 0) {
          setActualAge(ageNum);
          try {
            localStorage.setItem(
              ACTUAL_AGE_CACHE_KEY,
              JSON.stringify({ age: ageNum, timestamp: Date.now() })
            );
          } catch (_) {
            // Best-effort — quota errors / privacy mode shouldn't break
            // the dashboard.
          }
        }
      } catch (_) {
        // Non-critical; gauge falls back to default "better" tone.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="dashboard-page-wrapper">
      <Navbar />

      <div className="dashboard-container">
        {/* Init banner — invisible during NOTHING / idle, shows
            "computing…" while STARTED/RUNNING, shows a retry row on
            FAILED. Sits above the mood tracker so a status update is
            always at the top of the visible viewport. */}
        <ModelStatusBanner
          status={modelStatus}
          phase={modelPhase}
          onRetry={() => { refreshModel(); refreshOverview(); }}
        />

        <div className="dashboard-mood-row">
          <MoodTracker />
        </div>
        <div className="dashboard-grid">
          {/* Top Left Box: Smart Stack widget */}
          <div className="card empty-box dashboard-smartstack-card">
            <SmartStack overview={displayOverview} actualAge={actualAge} />
          </div>

          {/* Top Right Column */}
          <div className="right-column-stack">
            {/* Top Right Upper Box: Health Tips -> Insights page tips section */}
            <div
              className="card clickable-card dashboard-status-card"
              onClick={() => navigate('/insights?section=tips')}
            >
              <StatusWidget />
            </div>

            {/* Top Right Lower Box: Activities -> Link to Activities */}
            <div
              className="card clickable-card dashboard-recent-card"
              onClick={() => navigate('/activities')}
            >
              <RecentActivitiesWidget />
            </div>
          </div>

          {/* Bottom Box: Personal Insights -> Insights page insights section */}
          <div
            className="card bottom-card clickable-card"
            onClick={() => navigate('/insights?section=insights')}
          >
            <InsightsWidget />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Dashboard;